import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Select, Tooltip, Dropdown, Spin, Tabs } from 'antd';
import { useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import { useAuth } from '../../../providers/AuthProvider';
import { useAPI } from '../../../config/API';
import Project from '../../../models/Project';
import LoadingBanner from '../../../components/LoadingBanner';
import ErrorBanner from '../../../components/ErrorBanner';
import CancelProjectModal from '../CancelProjectModal';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { CirclePause, CircleX, CirclePlay, Lock, Pencil, EllipsisVertical } from 'lucide-react';
import { useMount, useUnmount } from "react-use";
import InfoTooltip from "../../../components/InfoTooltip";

const { Option, OptGroup } = Select;


const STATES = {
    'Pending Scope': 'Search Active',
    'Find Talent': 'Search Active',
    'Proposal': 'Search Active',
    'SOW': 'Search Active',
    'Drafts': 'Draft',
    'Active': 'Project Active',
    'Complete': 'Closed',
    'Cancelled': 'Cancelled',
}

// Maps tab/status names to their pipeline stage position (1-4)
const TAB_STAGE_FILL = { 'Find Talent': 1, 'Proposal': 2, 'SOW': 3, 'Active': 4 };

// Canonical display order for the "All" grouped view
const STATUS_DISPLAY_ORDER = ['Drafts', 'Pending Scope', 'Find Talent', 'Proposal', 'SOW', 'Active', 'Complete', 'Cancelled'];


// ==================== ProjectSearchSelect ====================
function ProjectSearchSelect({ placeholder, disabled, onChange }) {
    const [searchValue, setSearchValue] = useState('');
    const [selected, setSelected] = useState([]);
    const selectedItemsRef = useRef([]);
    const debounceRef = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const {
        data: searchResults,
        isFetching,
        isError: isSearchError,
        refetch: retrySearch,
    } = useAPI(['searchProjectKeywords', debouncedSearch], {
        enabled: debouncedSearch.length > 0,
    });

    const handleSearch = useCallback((value) => {
        setSearchValue(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(value);
        }, 300);
    }, []);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const options = useMemo(() => {
        if (!searchResults || !Array.isArray(searchResults)) return [];
        return searchResults.map(({ key, title, results }) => ({
            groupLabel: title,
            groupKey: key,
            items: (results || []).map(result => ({
                label: result.name,
                value: key + '-' + result.id,
                ...result,
                type: key,
            })),
        }));
    }, [searchResults]);

    const handleChange = useCallback((labeledValues) => {
        setSelected(labeledValues);
        setSearchValue('');
        setDebouncedSearch('');

        const allItems = options.flatMap(g => g.items);
        const updatedItems = labeledValues.map(lv => {
            const existing = selectedItemsRef.current.find(i => i.value === lv.value);
            if (existing) return existing;
            return allItems.find(i => i.value === lv.value) || { value: lv.value, label: lv.label };
        });
        selectedItemsRef.current = updatedItems;
        onChange?.(updatedItems);
    }, [options, onChange]);

    // Typing has happened but the debounced request hasn't fired yet
    const isWaitingToSearch = searchValue.length > 0 && searchValue !== debouncedSearch;

    const notFoundContent = useMemo(() => {
        if (isFetching || isWaitingToSearch) {
            return (
                <span className="flex items-center gap-2 text-slate-500">
                    <Spin size="small" /> Searching…
                </span>
            );
        }
        if (isSearchError) {
            return (
                <span className="flex flex-col items-start gap-1 py-1">
                    <span className="text-slate-600">We couldn't load search results.</span>
                    <button
                        type="button"
                        className="text-primary font-semibold hover:underline"
                        onClick={() => retrySearch()}
                    >
                        Try again
                    </button>
                </span>
            );
        }
        if (debouncedSearch.length > 0) return 'No projects match your search';
        return 'Type to search your projects';
    }, [isFetching, isWaitingToSearch, isSearchError, debouncedSearch, retrySearch]);

    return (
        <Select
            mode="multiple"
            showSearch
            labelInValue
            placeholder={placeholder}
            disabled={disabled}
            value={selected}
            searchValue={searchValue}
            onSearch={handleSearch}
            onChange={handleChange}
            filterOption={false}
            notFoundContent={notFoundContent}
            className="w-full"
            allowClear
        >
            {options.map(group => (
                <OptGroup key={group.groupKey} label={group.groupLabel}>
                    {group.items.map(item => (
                        <Option key={item.value} value={item.value}>
                            {item.label}
                        </Option>
                    ))}
                </OptGroup>
            ))}
        </Select>
    );
}

const ChevronRightIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3 h-3">
        <path d="m9 18 6-6-6-6" />
    </svg>
);

const EditIcon = () => (
    <svg className="w-[13px] h-[13px] text-slate-400 opacity-0 group-hover/row:opacity-100 hover:text-primary transition-all duration-150 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

// ==================== TabStageDots ====================
// Pipeline stage indicator dots shown above tab labels (e.g. Proposal = 2 dots filled)
function TabStageDots({ tab, isActive }) {
    const fill = TAB_STAGE_FILL[tab];
    if (!fill) return null;
    return (
        <div className="relative flex gap-1 items-center h-[5px] mb-0.5">
            <div className="absolute top-1/2 inset-x-[2.5px] h-[1.5px] -translate-y-1/2 bg-slate-300 rounded-sm" />
            {[0, 1, 2, 3].map(i => (
                <span
                    key={i}
                    className={`w-[5px] h-[5px] rounded-full relative z-10 ${
                        i < fill
                            ? isActive ? 'bg-violet-500' : 'bg-slate-500'
                            : 'bg-slate-300'
                    }`}
                />
            ))}
        </div>
    );
}

// ==================== ProgressDots ====================
// 4-dot pipeline stage indicator shown in project rows
function ProgressDots({ doneCount, showCurrent = true }) {
    return (
        <div className="flex items-center justify-center">
            <div className="relative flex gap-[7px] items-center">
                <div className="absolute top-1/2 inset-x-[5px] h-[2px] -translate-y-1/2 bg-slate-200 rounded-sm" />
                {[0, 1, 2, 3].map(i => {
                    const isDone = i < doneCount;
                    const isCurrent = showCurrent && i === doneCount;
                    return (
                        <span
                            key={i}
                            className={TB.mapClass('w-[11px] h-[11px] rounded-full relative z-10', {
                                'bg-violet-500': isDone || isCurrent,
                                'bg-slate-200': !isDone && !isCurrent,
                                'shadow-[0_0_0_2px_white,0_0_0_4px_rgba(124,92,245,0.4)]': isCurrent,
                            })}
                        />
                    );
                })}
            </div>
        </div>
    );
}

// ==================== MetricBlock ====================
// Centered stat: large value, label underneath, optional "N New" alert line
function MetricBlock({ value, label, newCount = 0 }) {
    return (
        <div className="flex flex-col items-center text-center">
            <span className={`text-[18px] font-black leading-none ${value === 0 ? 'text-slate-400' : 'text-black'}`}>
                {value}
            </span>
            <span className="text-[11px] text-slate-600 font-medium mt-[5px]">{label}</span>
            <span className="text-[10px] font-bold text-red-600 mt-1 min-h-[13px] leading-none">
                {newCount > 0 ? `${newCount} Updated` : ''}
            </span>
        </div>
    );
}

// ==================== WorkflowSteps ====================
// Renders the active CTA button for the current pipeline step
function WorkflowSteps({ detailLink, project, onToggleHoldProject }) {
    const steps = project?.shopSteps() || [];
    const { auth } = useAuth();

    const isAuthOwner = useMemo(() => {
        return auth.canManageClients() || auth?.id === project.clientOwner?.()?.id;
    }, [project, auth]);

    if (!steps?.length) return null;

    const activeStepIndex = steps.findIndex(s => s.isActive);
    const activeStep = steps[activeStepIndex];

    if (!activeStep) return null;

    if (project.is_on_hold) {
        return (
            <div>
                <button
                    type="button"
                    id={`full-btn-on-hold-project-${project.id}`}
                    className="w-full rounded-lg inline-flex items-center justify-center gap-1.5 text-[14px] font-medium py-2.5 px-5 whitespace-nowrap bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={onToggleHoldProject.isLoading || !isAuthOwner}
                    onClick={async (e) => {
                        e.stopPropagation();
                        const updatesValue = await project.confirmToggleOnHold({}, true);
                        if (!!updatesValue) {
                            // Errors are surfaced to the user via the mutation's onError toast
                            await onToggleHoldProject.mutateAsync({ project, ...updatesValue }).catch(() => {});
                        }
                    }}
                >
                    <CirclePlay className="h-4 w-4" />
                    <span>Resume Project</span>
                    <ChevronRightIcon />
                </button>
                {!isAuthOwner && (
                    <InfoTooltip target={`full-btn-on-hold-project-${project.id}`} type="dark">
                        <span className="text-xs text-white px-2 py-3">
                            <i>Only Project owners can resume projects</i>
                        </span>
                    </InfoTooltip>
                )}
            </div>
        );
    }

    const showCount = ['Choose Candidates', 'Choose Proposal'].includes(activeStep.name);
    const shortlistCount = project.shortlisted_candidates_count;

    return (
        <Link to={detailLink} onClick={e => e.stopPropagation()}>
            <button type="button" className="w-full rounded-lg inline-flex items-center justify-center gap-1.5 text-[14px] font-medium py-2.5 px-5 whitespace-nowrap bg-violet-600 hover:bg-violet-700 text-white transition-colors">
                <span>{activeStep.name}</span>
                {showCount && shortlistCount > 0 && (
                    <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[11px] bg-white/20">
                        {shortlistCount}
                    </span>
                )}
                <ChevronRightIcon />
            </button>
        </Link>
    );
}

// ==================== PriceColumn ====================
function PriceColumn({ project }) {
    const estimate = project.totalPeriodCost(false) || project.platform_cost;
    const hasPricing = estimate && estimate > 0;
    const isComplete = project.status === 'Complete';

    return (
        <div className="flex flex-col gap-1">
            <p className="text-[10px] font-semibold tracking-[0.05em] uppercase text-slate-600">
                {isComplete ? 'Final Price' : 'Est. Price'}
            </p>
            <p className={TB.mapClass('text-[15px] font-bold', {
                'text-black': hasPricing,
                'text-slate-400': !hasPricing,
            })}>
                {hasPricing ? TB.formatCurrency(estimate, true) : 'Pending'}
            </p>
        </div>
    );
}

// ==================== OverflowMenu ====================
function OverflowMenu({ project, canEdit, onCancelProject, isOpen, setIsOpen, onToggleHoldProject }) {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const toggleCancelMutation = useMutation(['cancelProjectMutation'], async props => {
        const response = await project.addToCancel(true);
        return response;
    }, {
        onError: e => {
            console.warn(e);
            TB.showServerErrorToast();
        },
    });

    const handleEditClick = (e) => {
        e.stopPropagation?.();
        if (project.isBidding?.()) {
            navigate(project.briefStep?.() || project.detailLink());
        } else {
            navigate(project.detailLink());
        }
    };

    const isAuthOwner = useMemo(() => {
        return auth.canManageClients() || auth?.id === project.clientOwner?.()?.id;
    }, [project, auth]);

    const disableEdit = ['Complete', 'Cancelled'].includes(project.displayStatus());
    const cancelDisabled = !project.canCancel() || !canEdit;

    const menu = (
        <ul className="bg-white min-w-[11rem] py-2 border border-gray-200 rounded font-primary">
            <li>
                <button
                    type="button"
                    className="flex items-center gap-1.5 w-full text-sm text-left py-2 px-6 font-normal hover:bg-gray-200 transition duration-150 disabled:opacity-70 disabled:hover:bg-transparent"
                    disabled={disableEdit}
                    onClick={(e) => {
                        setIsOpen(false);
                        handleEditClick(e);
                    }}
                >
                    <Pencil className="inline h-3.5 w-3.5 mr-1.5" /> Edit
                </button>
            </li>
            <li>
                <button
                    type="button"
                    id={`project-on-hold-${project.id}`}
                    className="flex items-center gap-1.5 w-full text-sm text-left py-2 px-6 font-normal hover:bg-gray-200 transition duration-150 disabled:opacity-70 disabled:hover:bg-transparent"
                    disabled={!project.canPutOnHold() || onToggleHoldProject.isLoading || !canEdit || !isAuthOwner}
                    onClick={async () => {
                        setIsOpen(false);
                        const updatesValue = await project.confirmToggleOnHold({}, true);
                        if (!!updatesValue) {
                            // Errors are surfaced to the user via the mutation's onError toast
                            await onToggleHoldProject.mutateAsync({ project, ...updatesValue }).catch(() => {});
                        }
                    }}
                >
                    {!project.canPutOnHold() && !project.is_on_hold && <Lock className="inline h-3.5 w-3.5 mr-1.5" />}
                    {(project.canPutOnHold() || project.is_on_hold) && (
                        project.is_on_hold
                            ? <CirclePlay className="inline h-3.5 w-3.5 mr-1.5" />
                            : <CirclePause className="inline h-3.5 w-3.5 mr-1.5" />
                    )}
                    {project.is_on_hold ? 'Resume Project' : 'Put on hold'}
                </button>
                {!isAuthOwner && (
                    <InfoTooltip target={`project-on-hold-${project.id}`} type="dark">
                        <span className="text-xs text-white px-2 py-3">
                            <i>Only Project owners can {project.is_on_hold ? 'resume projects' : 'put projects on hold'}</i>
                        </span>
                    </InfoTooltip>
                )}
            </li>
            <li>
                <button
                    type="button"
                    id={`project-cancel-${project.id}`}
                    className="flex items-center gap-1.5 w-full text-sm text-left py-2 px-6 font-normal text-danger hover:bg-gray-200 transition duration-150 disabled:opacity-70 disabled:hover:bg-transparent"
                    disabled={!project.canCancel() || !canEdit || !isAuthOwner}
                    onClick={async () => {
                        setIsOpen(false);
                        let confirm = await project.confirmCancelProject();
                        if (confirm) {
                            toggleCancelMutation.mutate({});
                        }
                    }}
                >
                    <CircleX className="inline h-3.5 w-3.5 mr-1.5" />
                    Cancel Project
                </button>
                {!isAuthOwner && (
                    <InfoTooltip target={`project-cancel-${project.id}`} type="dark">
                        <span className="text-xs text-white px-2 py-3">
                            <i>Only Project owners can cancel the project</i>
                        </span>
                    </InfoTooltip>
                )}
            </li>
            {auth?.canManageClients?.() && project.canCancel() && (
                <li>
                    <button
                        type="button"
                        className="flex items-center gap-1.5 w-full text-sm text-left py-2 px-6 font-normal text-danger hover:bg-gray-200 transition duration-150 disabled:opacity-70 disabled:hover:bg-transparent"
                        disabled={cancelDisabled}
                        onClick={() => {
                            setIsOpen(false);
                            onCancelProject?.(project);
                        }}
                    >
                        {cancelDisabled && <Lock className="inline h-3.5 w-3.5 mr-1.5" />}
                        {!cancelDisabled && <CircleX className="inline h-3.5 w-3.5 mr-1.5" />}
                        Close Project
                    </button>
                </li>
            )}
        </ul>
    );

    return (
        <div onClick={e => e.stopPropagation()} className="relative">
            {isOpen && (
                <div
                    onClick={() => setIsOpen(false)}
                    style={{
                        position: 'fixed', top: 0, left: 0,
                        width: '100vw', height: '100vh',
                        zIndex: 1049,
                    }}
                />
            )}
            <Dropdown
                open={isOpen}
                onOpenChange={open => setIsOpen(open)}
                overlay={menu}
                trigger={['click']}
                placement="bottomRight"
            >
                <a
                    onClick={() => setIsOpen(true)}
                    className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition rounded cursor-pointer"
                    aria-label="Project actions"
                >
                    <EllipsisVertical />
                </a>
            </Dropdown>
        </div>
    );
}

// ==================== ProjectRow ====================
function ProjectRow({ project, canEdit, onCancelProject, onToggleHoldProject, navigate }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const isDraft     = project.status === 'New';
    const isActive    = project.status === 'Active';
    const isCancelled = project.status === 'Cancelled';
    const isComplete  = project.status === 'Complete';
    const isBidding   = !isDraft && !isActive && !isCancelled && !isComplete;

    const groupName  = project.client_project_group?.name || 'Public';
    const isPublic   = !project.client_project_group || groupName === 'Public';
    const projectType = project.isBYO?.() ? 'Hire by Open Positions' : project.isService?.() ? 'Find Teams by Project Type' : '';

    const statusDotColor = (project.display_status === 'Drafts' || project.display_status === 'Cancelled') ? '#bcbec3' : '#16a34a';
    const statusLabel    = STATES[project.displayStatus?.() || project.status] || 'N/A';

    // Pipeline stage position for progress dots (0 = doneCount, current dot is doneCount index)
    const displayStatus   = project.displayStatus?.() || project.status;
    const stageFill       = isActive ? 4 : (TAB_STAGE_FILL[displayStatus] || 1);
    const progressDone    = Math.max(0, stageFill - 1);

    // Show red left accent when there are unseen proposal updates
    const hasNewActivity  = (project.unseen_updated_candidate_proposals_count > 0);

    const handleRowClick = () => {
        if (isCancelled) return;
        navigate(project.detailLink());
    };

    const handleNameClick = (e) => {
        e.stopPropagation();
        if (project.isBidding?.()) {
            navigate(project.briefStep?.() || project.detailLink());
        } else {
            navigate(project.detailLink());
        }
    };

    // ---- Shared sub-cells ----

    const titleCell = (
        <div>
            <div className="flex items-center gap-2 mb-1">
                <span className="font-black text-[16px] text-black tracking-tight line-clamp-2 leading-tight">
                    {project.name}
                </span>
                {!isCancelled && (
                    <button type="button" className="p-0.5 shrink-0" onClick={handleNameClick}>
                        <EditIcon />
                    </button>
                )}
            </div>
            <div className="flex items-center gap-2">
                <span className={TB.mapClass('px-2 py-0.5 rounded text-[11px] font-semibold', {
                    'bg-violet-50 text-violet-600': isPublic,
                    'bg-slate-100 text-slate-500': !isPublic,
                })}>
                    {groupName}
                </span>
                {projectType && <span className="text-[12px] text-slate-400">{projectType}</span>}
            </div>
        </div>
    );

    const statusCell = (
        <div className="lg:border-l border-slate-200 lg:pl-3.5 flex flex-col justify-center text-[12px]">
            <span className="flex items-center gap-2 font-black text-black mb-0.5">
                {project.is_on_hold && !isCancelled ? (
                    <>
                        <span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0" />
                        On Hold
                    </>
                ) : (
                    <>
                        <span className="w-2 h-2 rounded-full shrink-0 -mt-0.5" style={{ background: statusDotColor }} />
                        {statusLabel}
                    </>
                )}
            </span>
            {project.updated_at && (
                <Tooltip title={`Active ${TB.moment(project.updated_at).format('lll')}`}>
                    <span className="lg:whitespace-nowrap">
                        <span className="text-slate-600">Active: </span>
                        <span className="text-black font-black">{TB.moment(project.updated_at).fromNow()}</span>
                    </span>
                </Tooltip>
            )}
            <Tooltip title={`Created ${TB.moment(project.created_at).format('lll')}`}>
                <span className="lg:whitespace-nowrap">
                    <span className="text-slate-600">Created: </span>
                    <span className="text-black font-black">{TB.moment(project.created_at).fromNow()}</span>
                </span>
            </Tooltip>
        </div>
    );

    const menuCell = !isCancelled && (
        <OverflowMenu
            project={project}
            isOpen={isMenuOpen}
            setIsOpen={setIsMenuOpen}
            canEdit={canEdit}
            onCancelProject={onCancelProject}
            onToggleHoldProject={onToggleHoldProject}
        />
    );

    // ---- Base card classes ----
    const cardBase = TB.mapClass(
        'group/row relative bg-white rounded-xl px-5 py-4 lg:py-0 min-h-[80px] transition-all duration-150 overflow-hidden',
        {
            // Normal shadow + hover lift for non-cancelled rows
            'shadow-[0_1px_2px_rgba(20,20,40,0.05),0_2px_6px_rgba(20,20,40,0.04)] hover:shadow-[0_4px_18px_rgba(20,20,40,0.12)] hover:-translate-y-px cursor-pointer': !isCancelled,
            // Cancelled: flat, muted, no hover effects
            'opacity-50 shadow-none': isCancelled,
            'opacity-70 hover:opacity-100': project.is_on_hold && !isCancelled,
            '!opacity-100': isMenuOpen,
            // Red left accent for rows with new activity (never on cancelled)
            'before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-red-500': hasNewActivity && !isCancelled,
        }
    );

    // ---- Bidding (Find Talent / Proposal / SOW) ----
    // Mobile: 4-col grid (3×1fr + 28px menu). Title spans 3, menu pinned top-right, CTA full-width, price+status share bottom row.
    // Desktop: explicit 9-col grid, all col spans reset via lg:col-auto.
    if (isBidding) {
        return (
            <div
                className={`${cardBase} grid items-center gap-3 lg:gap-4 grid-cols-[1fr_1fr_1fr_28px] lg:grid-cols-[minmax(220px,1fr)_68px_68px_68px_76px_240px_88px_168px_28px]`}
                onClick={handleRowClick}
            >
                <div className="col-span-3 lg:col-auto">{titleCell}</div>
                <div><MetricBlock value={project.candidate_proposals_received_count || 0} label="Proposals" newCount={project.unseen_updated_candidate_proposals_count || 0} /></div>
                <div><MetricBlock value={project.messaged_candidates_count || 0} label="Messaged" /></div>
                <div><MetricBlock value={project.requested_meetings_count || 0} label="Interviews" /></div>
                <div className="hidden lg:flex items-center justify-center lg:pl-4"><ProgressDots doneCount={progressDone} /></div>
                <div className="col-span-4 lg:col-auto">
                    <WorkflowSteps detailLink={project.detailLink()} project={project} onToggleHoldProject={onToggleHoldProject} />
                </div>
                <div className="col-span-2 lg:col-auto lg:pl-[18px]"><PriceColumn project={project} /></div>
                <div className="col-span-2 lg:col-auto">{statusCell}</div>
                <div className="col-start-4 row-start-1 lg:col-auto lg:row-auto self-center pt-1">{menuCell}</div>
            </div>
        );
    }

    // ---- Active ----
    // Mobile: 3-col grid (2×1fr + 28px menu). Title spans 2, menu pinned top-right.
    if (isActive) {
        return (
            <div
                className={`${cardBase} grid items-center gap-3 lg:gap-4 grid-cols-[1fr_1fr_28px] lg:grid-cols-[minmax(220px,1fr)_140px_140px_56px_240px_88px_168px_28px]`}
                onClick={handleRowClick}
            >
                <div className="col-span-2 lg:col-auto">{titleCell}</div>
                {/* Date range */}
                <div className="flex flex-col gap-1.5 text-[12px]">
                    {project.anticipatedStart?.()?.isValid?.() && (
                        <p>
                            <span className="text-slate-500 w-10 inline-block">Start</span>
                            <span className="font-bold">{project.anticipatedStart().format('ll')}</span>
                        </p>
                    )}
                    {project.anticipatedEnd?.()?.isValid?.() && (
                        <p>
                            <span className="text-slate-500 w-10 inline-block">End</span>
                            <span className="font-bold">{project.anticipatedEnd().format('ll')}</span>
                        </p>
                    )}
                </div>
                {/* Counts */}
                <div className="flex flex-col gap-1.5 text-[12px] lg:pl-4">
                    <p>
                        <span className="text-slate-500 mr-2">Positions</span>
                        <span className={`font-bold ${!project.total_positions ? 'text-slate-300' : ''}`}>
                            {project.total_positions || 0}
                        </span>
                    </p>
                    <p>
                        <span className="text-slate-500 mr-2">Change Orders</span>
                        <span className={`font-bold ${!project.published_change_orders_count ? 'text-slate-300' : ''}`}>
                            {project.published_change_orders_count || 0}
                        </span>
                        {!!project.unpublished_change_orders_count && (
                            <span className="bg-red-50 ml-2 text-red-500 text-[9.5px] font-bold px-[5px] py-px rounded-[3px] tracking-wide uppercase whitespace-nowrap">
                                {project.unpublished_change_orders_count} Pending
                            </span>
                        )}
                    </p>
                </div>
                <div className="hidden lg:flex items-center justify-center"><ProgressDots doneCount={3} /></div>
                {/* CTA */}
                <div className="col-span-3 lg:col-auto">
                    <Link to={project.detailLink()} onClick={e => e.stopPropagation()}>
                        <button type="button" className="w-full rounded-lg inline-flex items-center justify-center gap-1.5 text-[14px] font-medium py-2.5 px-5 whitespace-nowrap bg-[#111418] hover:bg-[#1e2328] text-white transition-colors">
                            <span>View Project</span>
                            <ChevronRightIcon />
                        </button>
                    </Link>
                </div>
                <div className="lg:col-auto lg:pl-[18px]"><PriceColumn project={project} /></div>
                <div className="col-span-2 lg:col-auto">{statusCell}</div>
                <div className="col-start-3 row-start-1 lg:col-auto lg:row-auto self-center pt-1">{menuCell}</div>
            </div>
        );
    }

    // ---- Draft / Cancelled / Complete ----
    // Mobile: 3-col grid (2×1fr + 28px). Title spans 2, menu pinned top-right, middle full-width, CTA full-width, price+status share bottom row.
    return (
        <div
            className={`${cardBase} grid items-center gap-3 lg:gap-4 grid-cols-[1fr_1fr_28px] lg:grid-cols-[minmax(220px,1fr)_1fr_240px_88px_168px_28px]`}
            onClick={handleRowClick}
        >
            <div className="col-span-2 lg:col-auto">{titleCell}</div>
            {/* Middle info — full width on mobile */}
            <div className="col-span-3 lg:col-auto lg:border-l border-slate-100 lg:pl-4">
                {isDraft && (
                    <p className="text-xs text-slate-400">
                        Finish entering project information to be matched with talent
                    </p>
                )}
                {(isCancelled || isComplete) && (
                    <div className="flex flex-col gap-1.5 text-[12px]">
                        <p>
                            <span className="text-slate-400 mr-2">Positions</span>
                            <span className="font-bold">{project.total_positions || 0}</span>
                        </p>
                    </div>
                )}
            </div>
            {/* CTA — full width on mobile */}
            <div className="col-span-3 lg:col-auto">
                {isDraft && (
                    <Link to={project.detailLink()} onClick={e => e.stopPropagation()}>
                        <button type="button" className="w-full rounded-lg inline-flex items-center justify-center gap-1.5 text-[14px] font-medium py-2.5 px-5 whitespace-nowrap bg-violet-600 hover:bg-violet-700 text-white transition-colors">
                            <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-xl bg-white/20">+</span>
                            Finish Draft
                            <ChevronRightIcon />
                        </button>
                    </Link>
                )}
                {isComplete && (
                    <Link to={project.detailLink()} onClick={e => e.stopPropagation()}>
                        <button type="button" className="w-full rounded-lg inline-flex items-center justify-center gap-1.5 text-[14px] font-medium py-2.5 px-5 whitespace-nowrap bg-[#111418] hover:bg-[#1e2328] text-white transition-colors">
                            View Project
                            <ChevronRightIcon />
                        </button>
                    </Link>
                )}
            </div>
            <div className="lg:col-auto lg:pl-[18px]"><PriceColumn project={project} /></div>
            <div className="col-span-2 lg:col-auto">{statusCell}</div>
            <div className="col-start-3 row-start-1 lg:col-auto lg:row-auto self-center pt-1">{menuCell}</div>
        </div>
    );
}

// ==================== SectionHeader ====================
function SectionHeader({ title, count }) {
    const label = useMemo(() => {
        if (!emptyCopy?.[title] || !emptyCopy[title]?.label) {
            return `${TB.pluralize('Project', count, false)} ${TB.startCase(title)}`;
        }
        return emptyCopy[title]?.label(TB.pluralize('Project', count, false))
    }, [title, count])

    return (
        <div className="flex items-center gap-3 mt-8 mb-3 px-1 border-t border-slate-200 pt-4 first:border-t-0 first:mt-0 first:pt-0">
            <h2 className="text-[12px] font-medium text-slate-600 pl-2.5">
                <span className="font-bold text-slate-900">{count}</span> <span>{label}</span>
            </h2>
            <span className="flex-1 h-px bg-slate-200" />
        </div>
    );
}

// ==================== CPL (Client Projects List) ====================
const CPL = () => {
    const { status } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { auth } = useAuth();
    const queryClient = useQueryClient();

    const [cancelProject, setCancelProject] = useState(null);
    const [keywordFilters, setKeywordFilters] = useState({});
    // Bumped to remount ProjectSearchSelect, which keeps its selection in internal state
    const [searchResetKey, setSearchResetKey] = useState(0);
    const [dateFilter, setDateFilter] = useState(() => {
        const stored = TB.storedPreferences()?.date_range_filter;
        return parseInt(stored || TB.invert(laravel.date_limit_ranges)['Last 18 Months']);
    });

    const isToggling = useIsMutating({ mutationKey: ['toggleProjectHold'] }) > 0;
    const isTogglingCancel = useIsMutating({ mutationKey: ['cancelProjectMutation'] }) > 0;
    const isUpdatingProject = isToggling || isTogglingCancel;

    const uniqId = useRef(TB.uuid()).current;

    const onProjectUpdated = async (project) => {
        return queryClient.setQueriesData({
            predicate: ({ queryKey }) => queryKey?.[0] === 'fetchClientProjects'
        }, old => {
            if (!old) return old;
            return old.map(p => p.id === project.id ? project : p);
        });
    };

    useMount(() => TB.event().listen("Projects@updated", onProjectUpdated, uniqId));
    useUnmount(() => TB.event().provider().off("Projects@updated", onProjectUpdated, uniqId));

    const groups = useMemo(() => {
        const raw = TB.map(auth?.client_project_groups || [], group => ({ ...group, value: group.id }));
        if (raw.length > 0) raw.unshift({ name: 'Public (company-wide)', value: -1 });
        return raw;
    }, [auth?.client_project_groups]);

    const groupSlugToValue = useMemo(() => {
        const map = {};
        groups.forEach(g => { map[TB.slugify(g.name)] = g.value; });
        return map;
    }, [groups]);

    const groupValueToSlug = useMemo(() => {
        const map = {};
        groups.forEach(g => { map[g.value] = TB.slugify(g.name); });
        return map;
    }, [groups]);

    const selectedGroupId = useMemo(() => {
        const groupParam = searchParams.get('group');
        if (!groupParam) return undefined;
        return groupSlugToValue[groupParam];
    }, [searchParams, groupSlugToValue]);

    const queryParams = useMemo(() => {
        const params = TB.mapValues({ ...keywordFilters }, values => TB.pluck(values, 'id'));
        if (TB.isDefined(selectedGroupId)) params.group_id = selectedGroupId;
        if (TB.isDefined(dateFilter) && dateFilter !== '') params.date = dateFilter;
        return params;
    }, [keywordFilters, selectedGroupId, dateFilter]);

    const {
        data: fetchedProjects,
        isInitialLoading,
        isFetching,
        error,
        refetch: refetchProjects,
    } = useAPI(['fetchClientProjects', { ...queryParams, authId: auth.id }], {
        keepPreviousData: false,
        refetchOnMount: true,
        staleTime: 0
    });

    // Treat a retry-after-error as loading so the error banner doesn't sit
    // unchanged while the request is in flight
    const isLoading = isInitialLoading || (!!error && isFetching);
    const showError = !!error && !isFetching;

    const projects = useMemo(() => {
        if (!fetchedProjects) return [];
        const normalized = TB.map(fetchedProjects, project => {
            project.display_status = project.displayStatus();
            return project;
        });
        const statusOptions = TB.values(Project.Status);
        return TB.orderBy(normalized, project => statusOptions.indexOf(project.status), 'asc');
    }, [fetchedProjects, isUpdatingProject]);

    const onGroupChange = useCallback((value) => {
        const params = new URLSearchParams(searchParams);
        if (value === undefined || value === null) {
            params.delete('group');
        } else {
            const slug = groupValueToSlug[value];
            if (slug) params.set('group', slug);
        }
        setSearchParams(params, { replace: true });
    }, [searchParams, groupValueToSlug]);

    // Redirect manager users
    useEffect(() => {
        if (auth?.isClientManager?.() || auth?.isPlatformUser?.()) {
            navigate('/client/manager/projects');
        }
    }, [auth]);

    const statusParam = status;
    const activeTab = useMemo(() => {
        if (!statusParam) return 'All';
        if (statusParam === 'sow') return 'SOW';
        return TB.titleCase(statusParam.replace('-', ' '));
    }, [statusParam]);

    useEffect(() => {
        if (!statusParam || (statusParam && activeTab !== 'All' && !Project.statusOptions().includes(activeTab))) {
            return navigate('/clients/projects/all', { replace: true });
        }
    }, [status, activeTab, statusParam]);

    useEffect(() => {
        if (TB.isDefined(dateFilter) && dateFilter !== '') {
            TB.persistStoredPreferences({ date_range_filter: dateFilter });
        }
    }, [dateFilter]);

    const groupedProjects = useMemo(() => {
        return TB.groupBy(projects, 'display_status');
    }, [projects, isUpdatingProject]);

    const tabs = useMemo(() => {
        const result = {};
        for (const tabStatus of ['All', ...Project.statusOptions()]) {
            const tabTitle = Project.displayStatusFor(tabStatus);
            if (tabStatus === 'All') {
                result[tabTitle] = TB.flatten(Object.values(groupedProjects));
            } else {
                result[tabTitle] = groupedProjects[tabTitle] || [];
            }
        }
        return result;
    }, [groupedProjects, isUpdatingProject]);

    const visibleGroups = useMemo(() => {
        if (!statusParam || TB.toLower(activeTab) === 'all') return groupedProjects;
        const displayStatus = activeTab === 'Sow' ? 'SOW' : activeTab;
        return { [displayStatus]: tabs[displayStatus] || [] };
    }, [statusParam, activeTab, groupedProjects, tabs, isUpdatingProject]);

    const onKeywordsChange = useCallback((keywords) => {
        setKeywordFilters(TB.groupBy(keywords, 'type'));
    }, []);

    const hasActiveFilters = useMemo(() => {
        return TB.isDefined(selectedGroupId)
            || Object.values(keywordFilters).some(items => items?.length > 0);
    }, [selectedGroupId, keywordFilters]);

    const clearAllFilters = useCallback(() => {
        setKeywordFilters({});
        setSearchResetKey(key => key + 1);
        const params = new URLSearchParams(searchParams);
        params.delete('group');
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    const viewProjectsWithStatus = useCallback((tab) => {
        const slug = tab === 'SOW' ? 'sow' : TB.toLower(TB.slugify(tab));
        navigate(`/client/projects/${slug}`, { replace: true });
    }, [navigate]);

    const canEditProject = useCallback((project) => {
        return auth?.canEditProjectAndPositions?.(project);
    }, [auth]);

    const toggleProjectHoldMutation = useMutation({
        mutationKey: ['toggleProjectHold'],
        mutationFn: async ({ project, ...props }) => {
            const response = await project.requestToggleOnHold(props);
            return response;
        },
        onError: e => {
            console.warn(e);
            TB.showServerErrorToast();
        },
    });


    // ==================== Render ====================
    return (
        <div className="flex-grow flex flex-col bg[#FAFAFB]" key={`client-${auth?.clientOrg?.()?.id}-${dateFilter}`}>
            {isUpdatingProject && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50">
                    <LoadingSpinner size="lg" />
                </div>
            )}

            <div className="nav-container min-h-[unset]">
                <div className="nav-info">
                    <h2 className="nav-title">
                        {TB.trans('projects.client_dashboard.nav_title')}
                    </h2>
                </div>
                <Link to={'/client/project/setup/new'} className="btn btn-primary">
                    New Project
                </Link>
            </div>

            <div className="flex-grow justify-start flex flex-col items-stretch">
                {/* Tabs */}
                <div className="max-w-[1500px] mx-auto w-full px-4 sm:px-6 md:px-10 pt-0.5">

                    {/* Desktop tabs — antd Tabs for animated ink bar; content panel is hidden */}
                    <div className="hidden md:block wripple-project-tabs">
                        <style>{`
                            /* Purple active ink bar */
                            .wripple-project-tabs .ant-tabs-ink-bar { background-color: #7C5CF5; }
                            /* Active tab text */
                            .wripple-project-tabs .ant-tabs-tab-active .ant-tabs-tab-btn { color: #7C5CF5 !important; font-weight: 700; }
                            /* Default tab text */
                            .wripple-project-tabs .ant-tabs-tab .ant-tabs-tab-btn { color: #43434F; font-weight: 500; transition: color 0.12s; }
                            /* Hover text */
                            .wripple-project-tabs .ant-tabs-tab:not(.ant-tabs-tab-active) .ant-tabs-tab-btn:hover { color: #15151B; }
                            /* Gray hover bottom indicator on inactive tabs */
                            .wripple-project-tabs .ant-tabs-tab { position: relative; }
                            .wripple-project-tabs .ant-tabs-tab::after {
                                content: '';
                                position: absolute;
                                bottom: 0; left: 0; right: 0;
                                height: 2px;
                                background: transparent;
                                transition: background 0.12s;
                                pointer-events: none;
                            }
                            .wripple-project-tabs .ant-tabs-tab:not(.ant-tabs-tab-active):hover::after { background: #C2C4CF; }
                            /* Remove default nav bottom margin and hide content panel */
                            .wripple-project-tabs .ant-tabs-nav { margin-bottom: 0 !important; }
                            .wripple-project-tabs .ant-tabs-content-holder { display: none; }
                        `}</style>
                        <Tabs
                            centered
                            className="border-bottom-none [&_.ant-tabs-tab]:pb-0 [&_.ant-tabs-nav::before]:border-b-slate-300"
                            activeKey={activeTab}
                            onChange={(key) => viewProjectsWithStatus(key)}
                            items={Object.entries(tabs).map(([tab, items]) => {
                                const isTabActive = TB.toLower(tab) === TB.toLower(activeTab);
                                // Counts are unknown while loading or errored — don't show a misleading 0
                                const countsReady = !isLoading && !error;
                                const isEmpty = countsReady && items.length === 0;
                                return {
                                    key: tab,
                                    label: (
                                        <span className={TB.mapClass('h-[43px] justify-end flex flex-col items-center pt-1', {
                                            'opacity-40 cursor-default': isEmpty,
                                        })}>
                                            <TabStageDots tab={tab} isActive={isTabActive} />
                                            <span className="flex items-center gap-1.5 pb-1 mt-1.5 text-[13.5px]">
                                                {tab}
                                                {countsReady && (
                                                    <span className={TB.mapClass('text-[12.5px]', {
                                                        'text-violet-500 opacity-80': isTabActive,
                                                        'text-slate-400': !isTabActive,
                                                    })}>
                                                        {items.length}
                                                    </span>
                                                )}
                                            </span>
                                        </span>
                                    ),
                                };
                            })}
                        />
                    </div>

                    {/* Mobile tab select */}
                    <div className="md:hidden">
                        <Select
                            disabled={isLoading}
                            value={activeTab}
                            onChange={(value) => viewProjectsWithStatus(value)}
                            className="w-full"
                            filterOption={(input, option) =>
                                TB.toLower(option.key).includes(input.toLowerCase())
                            }
                        >
                            {Object.entries(tabs).map(([tab, items]) => (
                                <Option key={tab} value={tab} disabled={!isLoading && !error && items.length === 0}>
                                    {tab}{!isLoading && !error ? ` (${items.length})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-3 mt-3 items-stretch sm:items-center">
                        <div className="flex-1 sm:max-w-[360px]">
                            <ProjectSearchSelect
                                key={searchResetKey}
                                placeholder="Search by project name"
                                disabled={isLoading}
                                onChange={onKeywordsChange}
                            />
                        </div>

                        <div className="flex gap-3 items-center flex-1">
                            {groups.length > 0 && (
                                <Select
                                    showSearch
                                    placeholder="Filter by group"
                                    disabled={isLoading}
                                    onChange={onGroupChange}
                                    value={selectedGroupId}
                                    filterOption={(input, option) => TB.toLower(option.key).includes(input.toLowerCase())}
                                    className="min-w-[140px] sm:min-w-[180px] flex-1 sm:flex-none"
                                    allowClear
                                >
                                    {TB.map(groups, option => (
                                        <Option key={option.name} value={option.value}>{option.name}</Option>
                                    ))}
                                </Select>
                            )}

                            <Select
                                placeholder="Filter by Date"
                                disabled={isLoading}
                                onChange={async (val) => {
                                    await TB.persistStoredPreferences({ date_range_filter: val });
                                    setDateFilter(val);
                                }}
                                value={dateFilter}
                                className="min-w-[140px] sm:min-w-[180px] sm:ml-auto flex-1 sm:flex-none"
                            >
                                {TB.map(laravel.date_limit_ranges, (name, value) => (
                                    <Option key={name} value={parseInt(value)}>{name}</Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="max-w-[1500px] mx-auto w-full px-4 sm:px-6 md:px-10 pt-6 pb-12 md:pb-20">
                    {showError ? (
                        <ErrorBanner
                            title="We couldn't load your projects"
                            message="Something went wrong on our end.  Check your connection and try again — if the problem continues, contact support."
                            retryText="Try Again"
                            onRetry={refetchProjects}
                        />
                    ) : isLoading ? (
                        <LoadingBanner message="projects.client_dashboard.loading" />
                    ) : (
                        <>
                            <Helmet>
                                <title>{activeTab} - Your Projects | Wripple</title>
                            </Helmet>

                            {TB.flatten(TB.values(visibleGroups)).length === 0 ? (
                                <EmptyProjectsState
                                    tab={activeTab}
                                    hasFilters={hasActiveFilters}
                                    onClearFilters={clearAllFilters}
                                />
                            ) : (
                                // Render groups in canonical stage order; skip empty groups
                                STATUS_DISPLAY_ORDER
                                    .filter(status => visibleGroups[status]?.length > 0)
                                    .map(group => {
                                        const groupProjects = visibleGroups[group];

                                        return (
                                            <div key={`group-${group === 'SOW' ? 'sow' : TB.slugify(group)}`} className="mb-6">
                                                <SectionHeader title={group} count={groupProjects.length} />
                                                <div className="flex flex-col gap-[18px]">
                                                    {TB.orderBy(groupProjects, 'created_at', 'desc').map(project => (
                                                        <ProjectRow
                                                            key={project.id}
                                                            project={project}
                                                            canEdit={canEditProject(project)}
                                                            onCancelProject={setCancelProject}
                                                            navigate={navigate}
                                                            onToggleHoldProject={toggleProjectHoldMutation}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })
                            )}
                        </>
                    )}
                </div>
            </div>

            {auth?.canManageClients?.() && cancelProject && (
                <CancelProjectModal
                    project={cancelProject}
                    open={!!cancelProject}
                    onToggle={async () => setCancelProject(null)}
                />
            )}
        </div>
    );
};

export default CPL;

const emptyCopy = {
    'Drafts':        { title: 'No projects in draft',            label: (append) => `Draft ${append}`, desc: 'Projects in draft mode will appear here.' },
    'Pending Scope': { title: 'No projects pending scope',       label: (append) => `${append} Pending Scope`, desc: 'Projects awaiting scope definition will appear here.' },
    'Find Talent':   { title: 'No projects searching talent',    label: (append) => `${append} Need Talent Selection`, desc: 'Projects searching for talent will appear here.' },
    'Proposal':      { title: 'No projects with proposals',      label: (append) => `${append} Awaiting Candidate Review`, desc: 'Projects with proposals will appear here.' },
    'SOW':           { title: 'No projects with SOW',            label: (append) => `${append} Awaiting SOW Review`, desc: 'Projects with SOWs will appear here.' },
    'Active':        { title: 'No active projects',              label: (append) => `Active ${append}`, desc: 'Projects that have been kicked off will appear here.' },
    'Complete':      { title: 'No completed projects',           label: (append) => `Completed ${append}`, desc: 'Wrapped projects will appear here once delivered.' },
    'Cancelled':     { title: 'No cancelled projects',           label: (append) => `Cancelled ${append}`,desc: 'Cancelled projects will appear here.' },
    'default':       { title: 'No projects yet',                 label: (append) => append, desc: 'Projects in this stage will appear here.' },
};
const EmptyProjectsState = ({ tab, hasFilters, onClearFilters }) => {
    const copy = hasFilters
        ? { title: 'No projects match your filters', desc: 'Try a different search, group, or date range — or clear your filters to see everything.' }
        : (emptyCopy[tab || 'default'] || emptyCopy['default']);

    // Offer the "New Project" CTA only where creating one is the natural next step
    const showNewProjectCta = !hasFilters && ['All', 'Drafts'].includes(tab);

    return (
        <div className="flex flex-col items-center justify-center py-[80px] px-6 mt-14 bg-white rounded-xl shadow-[0_1px_2px_rgba(20,20,40,0.05),0_2px_6px_rgba(20,20,40,0.04)]">
            <div className="rounded-full mb-6 h-16 w-16 flex items-center justify-center text-primary bg-[#F1ECFC]">
                <svg width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
            </div>
            <h3 className="text-lg mb-2.5">{copy.title}</h3>
            <p className="text-slate-400">{copy.desc}</p>
            {hasFilters && (
                <button
                    type="button"
                    className="mt-6 rounded-lg inline-flex items-center justify-center gap-1.5 text-[14px] font-medium py-2.5 px-5 border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                    onClick={() => onClearFilters?.()}
                >
                    Clear All Filters
                </button>
            )}
            {showNewProjectCta && (
                <Link to="/client/project/setup/new" className="mt-6">
                    <button type="button" className="rounded-lg inline-flex items-center justify-center gap-1.5 text-[14px] font-medium py-2.5 px-5 whitespace-nowrap bg-violet-600 hover:bg-violet-700 text-white transition-colors">
                        <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-xl bg-white/20">+</span>
                        Start a New Project
                    </button>
                </Link>
            )}
        </div>
    );
};